import { useCallback } from 'react';

import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { popModal } from '@/redux/modals/actions';

type Props = {
  onSave: () => Promise<void> | void;
  onDiscard: () => void;
  onClose?: () => void;
  name?: string;
  subtitle?: string | React.ReactNode;
  title?: string;
  primaryText?: string;
  secondaryText?: string;
  warning?: React.ReactNode;
};

function ModalConfirmChanges({
  onSave,
  onDiscard,
  onClose,
  subtitle,
  title,
  primaryText,
  secondaryText,
  warning,
}: Props) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const _title = title || t('dialog.warningModal.title');
  const _subtitle = subtitle === undefined ? t('dialog.warningModal.subtitle') : subtitle;

  const _primaryText = primaryText || t('common.save');
  const _secondaryText = secondaryText || t('common.discard');

  const handleSave = useCallback(async () => {
    try {
      await onSave();
    } finally {
      dispatch(popModal());
    }
  }, [dispatch, onSave]);

  const handleDiscard = useCallback(() => {
    onDiscard();
    dispatch(popModal());
  }, [dispatch, onDiscard]);

  const handleClose = useCallback(
    (open: boolean) => {
      if (!open) {
        if (onClose) {
          onClose();
        }
        dispatch(popModal());
      }
    },
    [dispatch, onClose]
  );

  return (
    <Dialog open onOpenChange={handleClose}>
      <DialogContent className="gap-6 sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{_title}</DialogTitle>
          {_subtitle && <DialogDescription>{_subtitle}</DialogDescription>}
        </DialogHeader>
        {warning}
        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={handleDiscard}>
            {_secondaryText}
          </Button>
          <Button onClick={handleSave}>{_primaryText}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ModalConfirmChanges;
